import React, { useState, useRef, useEffect } from 'react'
import { FormControl } from '@mui/material'
import { FaBuilding } from 'react-icons/fa'
import MyInput from './MyInput'
import { useTranslation } from 'react-i18next'

interface VatInputProps {
  value: string
  onChange: (value: string) => void
  isInvalid?: boolean
  error?: string
}

const VatInput: React.FC<VatInputProps> = ({
  value,
  onChange,
  isInvalid,
  error,
}) => {
  const { t } = useTranslation()
  const [isFocused, setIsFocused] = useState(false)
  const [hasBeenFocused, setHasBeenFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const formatVatNumber = (input: string) => {
    const digits = input.replace(/\D/g, '')
    let result = 'BE ____ ___ ___'
    for (let i = 0; i < digits.length && i < 10; i++) {
      result = result.replace('_', digits[i])
    }
    return result
  }

  const getCursorPosition = (input: string) => {
    const digits = input.replace(/\D/g, '').length
    if (digits <= 4) return digits + 3
    if (digits <= 7) return digits + 4
    return digits + 5
  }

  const handleChange = (newValue: string) => {
    const digits = newValue.replace(/\D/g, '').slice(0, 10)
    const formattedValue = `BE${digits}`
    onChange(formattedValue)

    const cursorPosition = getCursorPosition(digits)
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition)
      }
    }, 0)
  }

  const handleFocus = () => {
    setIsFocused(true)
    setHasBeenFocused(true)

    setTimeout(() => {
      if (inputRef.current) {
        const cursorPosition = inputRef.current.value.indexOf('_')
        if (cursorPosition !== -1) {
          inputRef.current.setSelectionRange(cursorPosition, cursorPosition)
        }
      }
    }, 0)
  }

  const handleBlur = () => {
    setIsFocused(false)
    if (!/\d/.test(value)) {
      onChange('')
      setHasBeenFocused(false)
    }
  }

  const displayValue = hasBeenFocused ? formatVatNumber(value) : value

  useEffect(() => {
    if (isFocused && inputRef.current) {
      const cursorPosition = value.indexOf('_')
      if (cursorPosition !== -1) {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition)
      }
    }
  }, [isFocused, value])

  return (
    <FormControl fullWidth>
      <MyInput
        label={t('vat-number')}
        value={displayValue}
        onChange={handleChange}
        icon={<FaBuilding />}
        error={error}
        isInvalid={isInvalid}
        autoComplete='off'
        onFocus={handleFocus}
        onBlur={handleBlur}
        inputRef={inputRef}
        placeholder={isFocused || hasBeenFocused ? 'BE ____ ___ ___' : ''}
      />
      {error && (
        <span className='text-redpink absolute -bottom-2 left-8 text-xs mt-1 px-2 py-1 rounded'>
          {error}
        </span>
      )}
    </FormControl>
  )
}

export default VatInput
